import { useRef, useCallback, forwardRef, useImperativeHandle, useState, useMemo, useEffect } from "react";
import DOMPurify from "dompurify";
import CommentDot from "./CommentDot";
import { cn } from "@/lib/utils";

/**
 * Detects the intended width of an email from its HTML content.
 * Checks table width attributes and inline styles for max-width/width values.
 * Returns null if no valid width is found (will use default 800px max).
 */
const detectIsResponsive = (html: string): boolean => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Check 1: @media queries in <style> tags
  const styleTags = doc.querySelectorAll('style');
  for (const style of styleTags) {
    if (style.textContent && /@media\s/i.test(style.textContent)) {
      return true;
    }
  }

  // Check 2: If no table uses a fixed pixel width attribute (400-800px range),
  // the email is fluid and will naturally reflow
  const tables = doc.querySelectorAll('table[width]');
  let hasFixedPixelWidth = false;
  for (const table of tables) {
    const widthAttr = table.getAttribute('width') || '';
    if (widthAttr.includes('%')) continue;
    const width = parseInt(widthAttr, 10);
    if (width >= 400 && width <= 800) {
      hasFixedPixelWidth = true;
      break;
    }
  }

  if (!hasFixedPixelWidth) {
    return true;
  }

  return false;
};

const detectEmailWidth = (html: string): number | null => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  let maxWidth: number | null = null;

  // Check tables with width attribute
  const tables = doc.querySelectorAll('table[width]');
  for (const table of tables) {
    const width = parseInt(table.getAttribute('width') || '', 10);
    if (width >= 400 && width <= 800) {
      maxWidth = Math.max(maxWidth ?? 0, width);
    }
  }

  // Check inline max-width or width styles
  const elementsWithStyle = doc.querySelectorAll('[style]');
  for (const el of elementsWithStyle) {
    const style = el.getAttribute('style') || '';
    const maxWidthMatch = style.match(/max-width:\s*(\d+)px/i);
    if (maxWidthMatch) {
      const width = parseInt(maxWidthMatch[1], 10);
      if (width >= 400 && width <= 800) {
        maxWidth = Math.max(maxWidth ?? 0, width);
      }
    }
    const widthMatch = style.match(/(?:^|;)\s*width:\s*(\d+)px/i);
    if (widthMatch) {
      const width = parseInt(widthMatch[1], 10);
      if (width >= 400 && width <= 800) {
        maxWidth = Math.max(maxWidth ?? 0, width);
      }
    }
  }

  return maxWidth;
};

export interface CommentDotData {
  id: string;
  positionX: number;
  positionY: number;
}

interface HtmlEmailPreviewProps {
  htmlContent: string;
  commentDots?: CommentDotData[];
  highlightedCommentId?: string | null;
  pendingDotPosition?: { x: number; y: number } | null;
  onPositionClick?: (positionX: number, positionY: number, screenRect: DOMRect) => void;
  onDotClick?: (commentId: string, screenRect: DOMRect, positionX: number, positionY: number) => void;
  isReadOnly?: boolean;
  isMobilePreview?: boolean;
  isTestLinksEnabled?: boolean;
}

export interface HtmlEmailPreviewRef {
  scrollToPosition: (positionX: number, positionY: number) => void;
}

const HtmlEmailPreview = forwardRef<HtmlEmailPreviewRef, HtmlEmailPreviewProps>(({
  htmlContent,
  commentDots = [],
  highlightedCommentId,
  pendingDotPosition,
  onPositionClick,
  onDotClick,
  isReadOnly = false,
  isMobilePreview = false,
  isTestLinksEnabled = false,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [cursorPosition, setCursorPosition] = useState<{ x: number; y: number } | null>(null);
  const [isHoveringContent, setIsHoveringContent] = useState(false);
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  // Sanitize HTML content
  const sanitizedHtml = DOMPurify.sanitize(htmlContent, {
    ADD_TAGS: ["style"],
    ADD_ATTR: ["target", "style"],
    ALLOW_DATA_ATTR: true,
  });

  // Detect intended email width from HTML (used for both desktop max-width and mobile scaling)
  const detectedWidth = useMemo(() => detectEmailWidth(htmlContent), [htmlContent]);
  const isResponsive = useMemo(() => detectIsResponsive(htmlContent), [htmlContent]);
  const nativeWidth = detectedWidth ?? 600;

  // Only scale non-responsive emails in mobile mode
  const needsScale = isMobilePreview && !isResponsive;
  const scaleFactor = needsScale ? 340 / nativeWidth : 1;

  // Track content height for mobile scaling (only needed when using transform scale)
  useEffect(() => {
    if (!needsScale || !contentRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContentHeight(entry.contentRect.height);
      }
    });
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [needsScale, sanitizedHtml]);

  // Scroll to position - scrolls the parent container
  const scrollToPosition = useCallback((positionX: number, positionY: number) => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;
    
    // Find the scrollable parent
    const scrollParent = container.closest('.overflow-auto');
    if (!scrollParent) return;
    
    const contentHeight = content.offsetHeight;
    const targetY = (positionY / 100) * contentHeight;
    const containerRect = container.getBoundingClientRect();
    const scrollParentRect = scrollParent.getBoundingClientRect();
    const currentScrollTop = scrollParent.scrollTop;
    
    // Calculate where to scroll
    const targetScrollTop = currentScrollTop + containerRect.top - scrollParentRect.top + targetY - scrollParent.clientHeight / 2;
    
    scrollParent.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: "smooth",
    });
  }, []);

  // Expose scrollToPosition to parent via ref
  useImperativeHandle(ref, () => ({
    scrollToPosition,
  }), [scrollToPosition]);

  // Handle mouse move for custom cursor on overlay
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (isReadOnly) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setCursorPosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, [isReadOnly]);

  // Handle click on overlay to place comment dot
  const handleOverlayClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (isReadOnly || !onPositionClick) return;
    
    e.stopPropagation();
    const overlay = e.currentTarget;
    const rect = overlay.getBoundingClientRect();
    
    // Calculate position as percentage of total content
    const positionX = ((e.clientX - rect.left) / rect.width) * 100;
    const positionY = ((e.clientY - rect.top) / rect.height) * 100;
    
    onPositionClick(positionX, positionY, rect);
  }, [isReadOnly, onPositionClick]);

  return (
    <div 
      className={cn(
        "w-full",
        isMobilePreview && "min-w-[360px] max-w-[360px]"
      )}
      style={!isMobilePreview ? {
        maxWidth: detectedWidth ? `${detectedWidth + 20}px` : '820px'
      } : undefined}
    >
      <div className="bg-card rounded-2xl border border-foreground/10 shadow-xl overflow-hidden p-[10px]">
        {/* Email Content Container */}
        <div
          ref={containerRef}
          className="relative"
          style={needsScale ? {
            height: contentHeight ? `${contentHeight * scaleFactor}px` : 'auto',
            overflow: 'hidden',
          } : undefined}
        >
          {/* Email HTML rendered directly */}
          <div 
            ref={contentRef}
            className={cn(
              "bg-white email-content",
              isTestLinksEnabled && "test-links-enabled",
            )}
            style={{ 
              pointerEvents: isTestLinksEnabled ? 'auto' : 'none',
              ...(needsScale ? {
                width: `${nativeWidth}px`,
                transform: `scale(${scaleFactor})`,
                transformOrigin: 'top left',
              } : {}),
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />

          {/* Transparent Overlay for Click Handling - hidden when testing links */}
          {!isTestLinksEnabled && (
            <div
              className="absolute inset-0"
              style={{ 
                cursor: isReadOnly ? "default" : "none",
                pointerEvents: 'auto',
                touchAction: 'pan-x pan-y',
              }}
              onMouseMove={handleMouseMove}
              onMouseEnter={() => !isReadOnly && setIsHoveringContent(true)}
              onMouseLeave={() => {
                setIsHoveringContent(false);
                setCursorPosition(null);
              }}
              onClick={handleOverlayClick}
            >
              {/* Comment Dots */}
              {commentDots.map((dot) => (
                <div key={dot.id} className="pointer-events-auto">
                  <CommentDot
                    positionX={dot.positionX}
                    positionY={dot.positionY}
                    isHighlighted={highlightedCommentId === dot.id}
                    onClick={() => {
                      if (onDotClick && containerRef.current) {
                        const rect = containerRef.current.getBoundingClientRect();
                        onDotClick(dot.id, rect, dot.positionX, dot.positionY);
                      }
                    }}
                  />
                </div>
              ))}

              {/* Pending dot (during comment creation) */}
              {pendingDotPosition && (
                <CommentDot
                  positionX={pendingDotPosition.x}
                  positionY={pendingDotPosition.y}
                  isPending
                />
              )}

              {/* Custom Cursor */}
              {!isReadOnly && isHoveringContent && cursorPosition && (
                <div
                  className="absolute w-4 h-4 rounded-full bg-acid-green/60 border-2 border-acid-green pointer-events-none z-20 transition-transform duration-75"
                  style={{
                    left: cursorPosition.x,
                    top: cursorPosition.y,
                    transform: "translate(-50%, -50%)",
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

HtmlEmailPreview.displayName = "HtmlEmailPreview";

export default HtmlEmailPreview;
